import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/style.css';
import './styles/login.css';
import './styles/layout.css';
import './styles/territories.css';
import './styles/dashboard.css';
import './styles/history-page.css';
import './styles/mission-history-detail.css';
import './styles/settings.css';
import './styles/geography.css';

import * as Sentry from '@sentry/react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { lazy } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  createRoutesFromChildren,
  matchRoutes,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';

import { ProtectedRoute } from './components/Login/ProtectedRoute.tsx';
import { SelectedNodeProvider } from './contexts/node/SelectedNodeProvider.tsx';
import { SelectedVehicleProvider } from './contexts/vehicle/SelectedVehicleProvider.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { Login } from './pages/Login.tsx';
import { Territories } from './pages/Territories.tsx';
import { getErrorMessage } from './queries/errorHandling.ts';

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: error => {
      Sentry.captureException(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      Sentry.captureException(error);
      if (!mutation.meta?.showGlobalErrorToast && !mutation.meta?.errorToastKey) {
        return;
      }
      const fallbackKey = mutation.meta?.errorToastKey ?? 'errors.generic-mutation-fail';
      toast.error(getErrorMessage(error, fallbackKey));
    },
  }),
});
const RequestResetForm = lazy(() => import('./components/ResetPassword/RequestResetForm'));
const RequestPasswordChangeForm = lazy(
  () => import('./components/ResetPassword/RequestPasswordChangeForm'),
);
const ServerError = lazy(() => import('./pages/ServerError'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Settings = lazy(() => import('./pages/Settings'));
const AlertHistory = lazy(() => import('./pages/AlertHistory'));
const MissionHistory = lazy(() => import('./pages/MissionHistory'));
const MissionHistoryDetail = lazy(() => import('./pages/MissionHistoryDetail'));
const Statistics = lazy(() => import('./pages/Statistics.tsx'));
const Geography = lazy(() => import('./pages/Geography.tsx'));

Sentry.init({
  dsn: 'https://2537d2c2b9d7ff4e3cfd0ccd5e1c8953@sentry.tech.padam.io/19',
  environment: import.meta.env.VITE_ENV,
  enabled: import.meta.env.VITE_ENV !== 'LOCAL',
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  tracesSampleRate: 1,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1,
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export function App() {
  return (
    <>
      <ErrorBoundary FallbackComponent={ServerError}>
        <QueryClientProvider client={queryClient}>
          <SentryRoutes>
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Territories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/:territorySlug/"
              element={
                <ProtectedRoute>
                  <SelectedNodeProvider>
                    <SelectedVehicleProvider>
                      <Dashboard />
                    </SelectedVehicleProvider>
                  </SelectedNodeProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/alert-history/:territorySlug/"
              element={
                <ProtectedRoute>
                  <AlertHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mission-history/:territorySlug/"
              element={
                <ProtectedRoute>
                  <MissionHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mission-history/:territorySlug/detail/:missionId/"
              element={
                <ProtectedRoute>
                  <MissionHistoryDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/statistics/:territorySlug/"
              element={
                <ProtectedRoute>
                  <Statistics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/geography/:territorySlug/"
              element={
                <ProtectedRoute>
                  <Geography />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route path="/login/" element={<Login />} />
            <Route path="/reset-password/" element={<RequestResetForm />} />
            <Route path="/reset-password-change/*" element={<RequestPasswordChangeForm />} />
            <Route path="*" element={<NotFound />} />
          </SentryRoutes>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
        <ToastContainer />
      </ErrorBoundary>
    </>
  );
}

export default App;
